import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { ApiCallError } from '@/api/client';
import { useContactM365, useContactM365OutOfOffice, useRefreshContactM365, type M365Core, type M365OutOfOffice, type M365SignIn } from '@/api/cipp';
import { useUserBackup } from '@/api/backup';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { backupSaasCustomerPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Microsoft 365 card for a ticket's contact (side rail). Data comes from CIPP via
 * the contact's company → mapped tenant (Admin → CIPP mapping). Read-only.
 *
 * Renders nothing when the company has no mapped tenant or CIPP isn't configured —
 * most noise-free default for clients that aren't on M365 with us.
 */

const MFA_METHOD_LABELS: Record<string, string> = {
  microsoftAuthenticatorPush: 'Authenticator',
  softwareOneTimePasscode: 'Authenticator code',
  windowsHelloForBusiness: 'Windows Hello',
  mobilePhone: 'Phone',
  alternateMobilePhone: 'Alt phone',
  officePhone: 'Office phone',
  fido2: 'Security key',
  passKeyDeviceBound: 'Passkey',
  passKeyDeviceBoundAuthenticator: 'Passkey',
  email: 'Email',
  temporaryAccessPass: 'TAP',
  microsoftAuthenticatorPasswordless: 'Passwordless',
};

type Tone = 'good' | 'warn' | 'bad' | 'muted';

const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-success-soft text-success',
  warn: 'bg-warning-soft text-warning',
  bad: 'bg-destructive/10 text-destructive',
  muted: 'bg-muted text-muted-foreground',
};

function Pill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span title={title} className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold', TONE_CLASS[tone])}>
      {children}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[12.5px] text-foreground">{children}</div>
    </div>
  );
}

function Shell({ children, actions }: { children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      <header className="flex items-center justify-between border-b border-divider bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Microsoft 365</span>
        <span className="flex items-center gap-1 normal-case tracking-normal">{actions}</span>
      </header>
      {children}
    </section>
  );
}

export function M365UserCard({ contactId }: { contactId: number | null | undefined }) {
  const core = useContactM365(contactId);
  const ok = core.data?.state === 'ok';
  const ooo = useContactM365OutOfOffice(contactId, ok);
  const refresh = useRefreshContactM365();

  if (!contactId) return null;

  if (core.isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking up the contact in Microsoft 365…
        </div>
      </Shell>
    );
  }

  if (core.error) {
    // Module switched off on this server — stay out of the way.
    if (core.error instanceof ApiCallError && core.error.payload?.error?.code === 'CIPP_NOT_CONFIGURED') return null;
    return (
      <Shell>
        <div className="px-4 py-3 text-xs text-destructive">
          Couldn't reach CIPP: {core.error instanceof Error ? core.error.message : 'unknown error'}
        </div>
      </Shell>
    );
  }

  const data = core.data;
  if (!data || data.state === 'no-tenant') return null;

  const refreshBtn = (
    <button
      type="button"
      onClick={() => refresh.mutate(contactId)}
      disabled={refresh.isPending}
      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      title={`Refresh from CIPP (fetched ${fmtRelative(data.fetchedAt)})`}
      aria-label="Refresh Microsoft 365 details"
    >
      <RefreshCw className={cn('h-3 w-3', refresh.isPending && 'animate-spin')} />
    </button>
  );

  if (data.state !== 'ok' || !data.user) {
    return (
      <Shell actions={refreshBtn}>
        <div className="px-4 py-3 text-xs text-muted-foreground">
          {data.state === 'no-email'
            ? 'This contact has no email address in Autotask.'
            : <>No Microsoft 365 account for <span className="text-foreground">{data.email}</span> in {data.tenant?.displayName}.</>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      actions={<>
        {refreshBtn}
        <a
          href={data.user.cippUrl}
          target="_blank"
          rel="noreferrer"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Open this user in CIPP"
          aria-label="Open in CIPP"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      </>}
    >
      <AccountSection data={data} />
      <MfaRow mfa={data.mfa} error={data.errors.mfa} />
      <SignInSection signIns={data.signIns} error={data.errors.signIns} />
      <OutOfOffice query={ooo} />
      <MailboxSection mailbox={data.mailbox} error={data.errors.mailbox} />
      <BackupRow tenantId={data.tenant?.customerId} objectId={data.user.id} />
      <div className="border-t border-divider px-4 py-1.5 text-[10.5px] text-muted-foreground/80">
        {data.tenant?.displayName} · via CIPP
      </div>
    </Shell>
  );
}

function AccountSection({ data }: { data: M365Core }) {
  const u = data.user!;
  return (
    <>
      <div className="px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium">{u.displayName}</span>
          {u.accountEnabled ? <Pill tone="good">Enabled</Pill> : <Pill tone="bad">Sign-in blocked</Pill>}
          {data.mfa?.isAdmin && <Pill tone="warn" title="Holds an admin role in the tenant">Admin</Pill>}
          {u.userType === 'Guest' && <Pill tone="muted">Guest</Pill>}
          {u.syncedFromOnPrem && <Pill tone="muted" title="Synced from on-premises AD — change passwords there">AD synced</Pill>}
        </div>
        <div className="truncate text-[11.5px] text-muted-foreground" title={u.upn}>{u.upn}</div>
        {(u.jobTitle || u.department) && (
          <div className="truncate text-[11.5px] text-muted-foreground">{[u.jobTitle, u.department].filter(Boolean).join(' · ')}</div>
        )}
      </div>
      <Row label="Licences">
        {u.licences.length === 0
          ? <span className="text-muted-foreground">Unlicensed</span>
          : <span className="leading-snug">{u.licences.join(', ')}</span>}
      </Row>
    </>
  );
}

function MfaRow({ mfa, error }: { mfa: M365Core['mfa']; error?: string }) {
  if (error) return <Row label="MFA"><span className="text-muted-foreground">Unavailable</span></Row>;
  if (!mfa) return <Row label="MFA"><span className="text-muted-foreground">Not in CIPP's report</span></Row>;

  const methods = [...new Set(mfa.methods.map((m) => MFA_METHOD_LABELS[m] ?? m))];
  const enforced = mfa.coveredBySecurityDefaults === true
    ? 'Security defaults'
    // Only "Enforced - …" means CA applies; "…licensing required for Conditional Access reporting" doesn't.
    : /^enforced/i.test(mfa.coveredByCA ?? '') ? `CA: ${mfa.coveredByCA}`
    : mfa.perUser === 'enforced' || mfa.perUser === 'enabled' ? `Per-user (${mfa.perUser})`
    : null;

  return (
    <Row label="MFA">
      <div className="flex flex-col items-end gap-0.5">
        {mfa.registered === true ? <Pill tone="good">Registered</Pill>
          : mfa.registered === false ? <Pill tone="bad">Not registered</Pill>
          : <Pill tone="muted">Unknown</Pill>}
        {methods.length > 0 && <span className="text-[11.5px] text-muted-foreground">{methods.join(', ')}</span>}
        <span className={cn('text-[11px]', enforced ? 'text-muted-foreground' : 'text-warning')}>
          {enforced ?? 'Not enforced by policy'}
        </span>
      </div>
    </Row>
  );
}

function signInLine(s: M365SignIn) {
  return [s.app, s.location].filter(Boolean).join(' · ');
}

function SignInSection({ signIns, error }: { signIns: M365SignIn[]; error?: string }) {
  const [open, setOpen] = useState(false);
  if (error) return <Row label="Sign-in"><span className="text-muted-foreground">Unavailable</span></Row>;
  if (signIns.length === 0) return <Row label="Sign-in"><span className="text-muted-foreground">None in the last 30 days</span></Row>;

  const last = signIns[0];
  const lastFailure = signIns.find((s) => !s.success);
  const failures = signIns.filter((s) => !s.success).length;

  return (
    <>
      <Row label="Sign-in">
        <div className="flex flex-col items-end">
          <span title={format(new Date(last.at), 'PPpp')}>
            {last.success ? fmtRelative(last.at) : <span className="text-destructive">Failed {fmtRelative(last.at)}</span>}
          </span>
          <span className="text-[11.5px] text-muted-foreground">{signInLine(last)}</span>
          {!last.success && last.failureReason && <span className="text-[11px] text-destructive">{last.failureReason}</span>}
          {last.success && lastFailure && (
            <span className="text-[11px] text-warning" title={lastFailure.failureReason ?? undefined}>
              {failures} failed of last {signIns.length}
            </span>
          )}
        </div>
      </Row>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 border-t border-divider px-4 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Recent sign-ins ({signIns.length})
      </button>
      {open && (
        <ul className="divide-y divide-divider border-t border-divider">
          {signIns.map((s, i) => (
            <li key={`${s.at}-${i}`} className="px-4 py-1.5 text-[11.5px]">
              <div className="flex items-center justify-between gap-2">
                <span className={s.success ? 'text-foreground' : 'text-destructive'}>
                  {s.success ? '✓' : '✕'} {s.app ?? 'Unknown app'}
                </span>
                <span className="shrink-0 text-muted-foreground" title={format(new Date(s.at), 'PPpp')}>{fmtRelative(s.at)}</span>
              </div>
              <div className="truncate text-muted-foreground" title={[s.device, s.ip].filter(Boolean).join(' · ')}>
                {[s.location, s.device].filter(Boolean).join(' · ') || s.ip}
              </div>
              {!s.success && s.failureReason && <div className="text-destructive">{s.failureReason}</div>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function OutOfOffice({ query }: { query: { data?: M365OutOfOffice; isLoading: boolean } }) {
  // Quiet while loading or when off — it's the one live Exchange call, so it never blocks the card.
  const o = query.data?.ooo;
  const on = o && (o.state === 'Enabled' || (o.state === 'Scheduled' && (!o.end || new Date(o.end) > new Date())));
  if (!on) return null;
  return (
    <div className="border-t border-divider bg-info-soft/50 px-4 py-2 text-[11.5px]">
      <div className="font-medium text-info">
        Out of office{o.state === 'Scheduled' && o.end ? ` until ${format(new Date(o.end), 'd MMM, h:mm a')}` : ''}
      </div>
      {o.message && <div className="mt-0.5 line-clamp-3 whitespace-pre-line text-muted-foreground">{o.message}</div>}
    </div>
  );
}

function MailboxSection({ mailbox: mb, error }: { mailbox: M365Core['mailbox']; error?: string }) {
  if (!mb) return <Row label="Mailbox"><span className="text-muted-foreground">{error ? 'Unavailable' : 'No mailbox'}</span></Row>;
  const usage = mb.sizeGb != null && mb.quotaGb ? mb.sizeGb / mb.quotaGb : null;

  return (
    <>
      {mb.forwardingTo && (
        <div className="flex items-start gap-2 border-t border-divider bg-destructive/5 px-4 py-2 text-[11.5px] text-destructive">
          <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
          <span>
            Mail forwards to <span className="font-medium">{mb.forwardingTo}</span>
            {mb.forwardAndDeliver ? ' (copy kept)' : ' (no copy kept)'}
          </span>
        </div>
      )}
      <Row label="Mailbox">
        <div className="flex flex-col items-end gap-0.5">
          <span>
            {mb.type === 'UserMailbox' ? 'User' : mb.type === 'SharedMailbox' ? 'Shared' : mb.type ?? '—'}
            {mb.sizeGb != null && (
              <span
                className={cn('ml-1.5 text-[11.5px]', usage != null && usage >= 0.9 ? 'text-destructive' : usage != null && usage >= 0.75 ? 'text-warning' : 'text-muted-foreground')}
                title={mb.asOf ? `From CIPP's report, ${fmtRelative(mb.asOf)}` : undefined}
              >
                {mb.sizeGb.toFixed(1)}{mb.quotaGb ? ` / ${Math.round(mb.quotaGb)} GB` : ' GB'}
              </span>
            )}
          </span>
          {(mb.holds.length > 0 || mb.archive) && (
            <span className="text-[11px] text-muted-foreground">
              {[mb.holds.length ? `${mb.holds.join(', ')} hold` : null, mb.archive ? 'Archive on' : null].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      </Row>
    </>
  );
}

/**
 * Datto SaaS Protection seat for this user (by Entra object id), from the Backup module's cache —
 * no extra Datto call. Hidden when backup isn't configured or the tenant isn't a SaaS customer.
 */
function BackupRow({ tenantId, objectId }: { tenantId: string | undefined; objectId: string }) {
  const q = useUserBackup(tenantId, objectId);
  const b = q.data;
  if (q.isLoading) return <Row label="Backup"><Loader2 className="ml-auto h-3 w-3 animate-spin text-muted-foreground" /></Row>;
  if (!b || q.error || b.state === 'no-customer') return null;
  if (b.state === 'loading') return <Row label="Backup"><span className="text-muted-foreground">Loading from Datto…</span></Row>;
  const link = b.saasCustomerId ? backupSaasCustomerPath(b.saasCustomerId) : null;
  const seat = b.seat;
  const protectedNow = seat && (seat.state === 'Active' || seat.state === 'Paused');
  const ex = b.exchange;
  return (
    <Row label="Backup">
      <div className="flex flex-col items-end gap-0.5">
        {!seat ? <Pill tone="bad" title="No Datto SaaS Protection seat for this user">Not protected</Pill>
          : <Pill tone={seat.state === 'Active' ? 'good' : seat.state === 'Unprotected' ? 'bad' : 'warn'} title={`Datto SaaS Protection seat: ${seat.state}`}>
              {seat.state === 'Active' ? 'Protected' : seat.state}
            </Pill>}
        {protectedNow && ex && (
          <span className={cn('text-[11px]', ex.health === 'healthy' ? 'text-muted-foreground' : 'text-warning')}
            title="Tenant-level Exchange backup health (Datto has no per-mailbox times)">
            Exchange {ex.lastFullyProtected ? `fully backed up ${fmtRelative(ex.lastFullyProtected)}` : 'not fully backed up in 7+ days'}
          </span>
        )}
        {link && <Link to={link} className="text-[11px] text-muted-foreground hover:text-foreground hover:underline">SaaS Protection →</Link>}
      </div>
    </Row>
  );
}
